import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;

// Debug logging removed since we're using backend endpoint

// Create Supabase client with service role for API key operations
const supabase = createClient(supabaseUrl, supabaseServiceKey);

/**
 * Store API key information in Supabase
 * @param {Object} keyData - API key data from Unkey
 * @param {string} userId - Clerk user ID
 * @param {string} userName - User's name
 * @param {string} userEmail - User's email
 * @returns {Promise<Object>} - Stored key data
 */
export const storeApiKey = async (keyData, userId, userName, userEmail) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .upsert([
        {
          key_id: keyData.keyId,
          user_id: userId,
          user_name: userName,
          user_email: userEmail,
          key_prefix: keyData.key?.substring(0, 8) || 'arms_',
          created_at: new Date().toISOString(),
          is_active: true,
          last_used: null,
          usage_count: 0,
          metadata: keyData.meta || {}
        }
      ], {
        onConflict: 'key_id',
        ignoreDuplicates: false
      })
      .select()
      .single();

    if (error) {
      console.error('Error storing API key in Supabase:', error);
      throw new Error(`Failed to store API key: ${error.message}`);
    }

    console.log('✅ API key stored in Supabase:', data);
    return data;
  } catch (error) {
    console.error('Error in storeApiKey:', error);
    throw error;
  }
};

/**
 * Get all API keys for a user
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Array>} - Array of API keys
 */
export const getUserApiKeys = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching API keys from Supabase:', error);
      throw new Error(`Failed to fetch API keys: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserApiKeys:', error);
    throw error;
  }
};

/**
 * Update API key usage statistics
 * @param {string} keyId - Unkey key ID
 * @param {Object} usageData - Usage data from Unkey
 * @returns {Promise<Object>} - Updated key data
 */
export const updateApiKeyUsage = async (keyId, usageData) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .update({
        last_used: new Date().toISOString(),
        usage_count: usageData.usage_count || 0,
        remaining_requests: usageData.remaining,
        total_requests: usageData.total,
        metadata: usageData.meta || {}
      })
      .eq('key_id', keyId)
      .select()
      .single();

    if (error) {
      console.error('Error updating API key usage in Supabase:', error);
      throw new Error(`Failed to update API key usage: ${error.message}`);
    }

    console.log('✅ Updated API key usage in Supabase:', data);
    return data;
  } catch (error) {
    console.error('Error in updateApiKeyUsage:', error);
    throw error;
  }
};

/**
 * Deactivate an API key (soft delete)
 * @param {string} keyId - Unkey key ID
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Object>} - Updated key data
 */
export const deactivateApiKey = async (keyId, userId) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .update({
        is_active: false,
        deactivated_at: new Date().toISOString()
      })
      .eq('key_id', keyId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error deactivating API key in Supabase:', error);
      throw new Error(`Failed to deactivate API key: ${error.message}`);
    }

    console.log('✅ Deactivated API key in Supabase:', data);
    return data;
  } catch (error) {
    console.error('Error in deactivateApiKey:', error);
    throw error;
  }
};

/**
 * Get API key by key ID
 * @param {string} keyId - Unkey key ID
 * @returns {Promise<Object>} - API key data
 */
export const getApiKeyById = async (keyId) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('key_id', keyId)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error fetching API key from Supabase:', error);
      throw new Error(`Failed to fetch API key: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in getApiKeyById:', error);
    throw error;
  }
};

/**
 * Get API key usage statistics for a user
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Object>} - Usage statistics
 */
export const getUserApiKeyStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .select('usage_count, remaining_requests, total_requests, created_at')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching API key stats from Supabase:', error);
      throw new Error(`Failed to fetch API key stats: ${error.message}`);
    }

    const stats = {
      total_keys: data.length,
      total_usage: data.reduce((sum, key) => sum + (key.usage_count || 0), 0),
      active_keys: data.filter(key => key.is_active).length,
      oldest_key: data.length > 0 ? Math.min(...data.map(key => new Date(key.created_at))) : null
    };

    return stats;
  } catch (error) {
    console.error('Error in getUserApiKeyStats:', error);
    throw error;
  }
};

/**
 * Get user's organization from Supabase
 * @param {string} userId - Clerk user ID
 * @returns {Promise<string|null>} - Organization name or null
 */
export const getUserOrganization = async (userId) => {
  try {
    // First try to get from user_organizations table if it exists
    const { data: orgData, error: orgError } = await supabase
      .from('user_organizations')
      .select('organization_name')
      .eq('user_id', userId)
      .single();

    if (!orgError && orgData) {
      console.log('Found organization in user_organizations table:', orgData.organization_name);
      return orgData.organization_name;
    }

    // Fallback: try to get from api_keys table metadata
    const { data: keyData, error: keyError } = await supabase
      .from('api_keys')
      .select('metadata')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    if (!keyError && keyData && keyData.metadata && keyData.metadata.organization) {
      console.log('Found organization in API key metadata:', keyData.metadata.organization);
      return keyData.metadata.organization;
    }

    // If no organization found, return null
    console.log('No organization found for user:', userId);
    return null;
  } catch (error) {
    console.error('Error getting user organization from Supabase:', error);
    return null;
  }
};

export default supabase; 